import {
  PDF_BORDER,
  type ReportTableCell,
  type ReportTableProps,
} from '@/components';
import type {
  ReportFundBusinessElementModel,
  ReportFundInvestmentModel,
  projectDetailUrlTypeEnum,
} from '@/features/mold/models';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import { BusinessFormLabelModifier } from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import {
  SPACE_TEXT,
  formatAmountForPdf,
  formatProjectEndYearForPdf,
  formatProjectStartYearForPdf,
} from '@/utils';
import type { CSSProperties } from 'react';
import type { z } from 'zod';
import { centerTextStyle } from '../pdf/pdf-utils';
import { fundBusinessValueConverter } from './converter';

// ////////////////////////////////////////
/// PDF
/// ////////////////////////////////////////

const rowBaseStyle: CSSProperties = {
  padding: 0.25,
};
const rowTextBaseStyle: CSSProperties = {
  textAlign: 'center',
};
const amountTextStyle: CSSProperties = {
  ...rowTextBaseStyle,
  textAlign: 'right',
};

const AMOUNT_CELL_RATIOS = '70px' as const;
const columnRatios = {
  // 同じレベルの数値合計は100になるようにする
  fundSheetNumber: 3,
  ministry: 10,
  fundOwner: 10,
  fundName: 10,
  startYear: 5,
  endYear: 5,
  investmentFromPrivateSector: AMOUNT_CELL_RATIOS,
  fundBusiness: {
    total: [AMOUNT_CELL_RATIOS, '420px'],
  },
  projectOverview: 47,
};

export const getInvestmentFundPdfHeader = (
  last3FiscalYears: number[],
): ReportTableProps['header'] => {
  return [
    {
      type: 'text',
      value: t('report-investment-fund-header-fund-sheet-number'),
      ratio: columnRatios.fundSheetNumber,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('ministry'),
      ratio: columnRatios.ministry,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('report-investment-fund-header-fund-owner'),
      ratio: columnRatios.fundOwner,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('fund-name'),
      ratio: columnRatios.fundName,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('report-investment-fund-header-start-year'),
      ratio: columnRatios.startYear,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: t('report-investment-fund-header-end-year'),
      ratio: columnRatios.endYear,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'flex',
        borderWidth: '0',
        flexCells: [
          {
            ...centerTextStyle,
            value: t('report-investment-fund-header-investment-from-goverment'),
            textStyle: rowTextBaseStyle,
          },
          {
            type: 'multiple',
            cell: {
              vertical: true,
              cellRatios: [1, 4],
              flexCells: [
                {
                  value: SPACE_TEXT,
                  wrapperStyle: {
                    borderLeft: 'none',
                    width: AMOUNT_CELL_RATIOS,
                  },
                },
                {
                  type: 'multiple',
                  cell: {
                    type: 'flex',
                    flexCells: last3FiscalYears.map((year) => ({
                      value: t(
                        'report-investment-fund-header-investment-amount',
                        {
                          year,
                        },
                      ),
                      textStyle: rowTextBaseStyle,
                      wrapperStyle: {
                        width: AMOUNT_CELL_RATIOS,
                        display: 'flex',
                      },
                      textWrapperStyle: { width: '100%' },
                    })),
                  },
                  wrapperStyle: {
                    borderLeft: PDF_BORDER.THIN,
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      type: 'text',
      value: t('report-investment-fund-header-investment-from-private-sector'),
      ratio: 'auto',
      textStyle: rowTextBaseStyle,
      textWrapperStyle: {
        width: columnRatios.investmentFromPrivateSector,
      },
    },
    {
      type: 'multiple',
      ratio: 'auto',
      cell: {
        type: 'flex',
        borderWidth: '0',
        flexCells: [
          {
            ...centerTextStyle,
            value: t('report-investment-fund-header-business-form'),
            textStyle: rowTextBaseStyle,
            textWrapperStyle: {
              width: AMOUNT_CELL_RATIOS,
            },
          },
          {
            type: 'multiple',
            cell: {
              type: 'flex',
              vertical: true,
              cellRatios: [1, 4],
              flexCells: [
                {
                  type: 'text',
                  value: t('report-investment-fund-header-fund-business'),
                  textStyle: rowTextBaseStyle,
                },
                {
                  type: 'multiple',
                  wrapperStyle: {
                    borderLeft: PDF_BORDER.THIN,
                  },
                  cell: {
                    type: 'flex',
                    flexCells: last3FiscalYears.map((year, i) => {
                      const isLastElement = i === last3FiscalYears.length - 1;
                      return {
                        type: 'multiple',
                        cell: {
                          type: 'flex',
                          vertical: true,
                          cellRatios: [1, 3],
                          wrapperStyle: {
                            width: '140px',
                          },
                          flexCells: [
                            {
                              value: `${t('report-investment-fund-year', { year })}${
                                isLastElement ? `（${t('estimate')}）` : ''
                              }`,
                              textStyle: rowTextBaseStyle,
                            },
                            {
                              type: 'multiple',
                              cell: {
                                type: 'flex',
                                flexCells: [
                                  {
                                    value: t(
                                      'report-investment-fund-header-investment-decision',
                                    ),
                                    textStyle: rowTextBaseStyle,
                                    wrapperStyle: {
                                      width: AMOUNT_CELL_RATIOS,
                                      display: 'flex',
                                    },
                                    textWrapperStyle: { width: '100%' },
                                  },
                                  {
                                    ...centerTextStyle,
                                    value: t(
                                      'report-investment-fund-header-investment-execution',
                                    ),
                                    textStyle: rowTextBaseStyle,
                                    wrapperStyle: {
                                      width: AMOUNT_CELL_RATIOS,
                                      display: 'flex',
                                    },
                                    textWrapperStyle: { width: '100%' },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      };
                    }),
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      type: 'text',
      value: t('project-overview'),
      ratio: columnRatios.projectOverview,
      textStyle: rowTextBaseStyle,
    },
  ];
};

const fundBusinessColumnsCellRatios = [
  AMOUNT_CELL_RATIOS,
  '71px',
  AMOUNT_CELL_RATIOS,
  '71px',
  AMOUNT_CELL_RATIOS,
  '71px',
  AMOUNT_CELL_RATIOS,
];

// 支援等に関する実績
const getFundBusinessColumns = (
  fundBusiness: z.infer<typeof ReportFundBusinessElementModel> | undefined,
  isDebugMode: boolean,
): ReportTableCell[] => {
  const value = fundBusinessValueConverter({
    fundBusiness,
    type: 'pdf',
    isDebugMode,
  });
  return [
    {
      ...centerTextStyle,
      value: fundBusiness?.businessForm
        ? BusinessFormLabelModifier[fundBusiness.businessForm]
        : null,
      textStyle: rowTextBaseStyle,
    },
    {
      ...centerTextStyle,
      value: `(${value.investmentDecisionCount2YearsBefore})\n${
        value.investmentDecisionAmount2YearsBefore
      }`,
      textStyle: amountTextStyle,
    },
    {
      ...centerTextStyle,
      value: `(${value.investmentExecutionCount2YearsBefore})\n${
        value.investmentExecutionAmount2YearsBefore
      }`,
      textStyle: amountTextStyle,
    },
    {
      ...centerTextStyle,
      value: `(${
        value.investmentDecisionCount1YearBefore
      })\n${value.investmentDecisionAmount1YearBefore}`,
      textStyle: amountTextStyle,
    },
    {
      ...centerTextStyle,
      value: `(${value.investmentExecutionCount1YearBefore})\n${
        value.investmentExecutionAmount1YearBefore
      }`,
      textStyle: amountTextStyle,
    },
    {
      ...centerTextStyle,
      value: `(${
        value.investmentDecisionCountCurrentYear
      })\n${value.investmentDecisionAmountCurrentYear}`,
      textStyle: amountTextStyle,
    },
    {
      ...centerTextStyle,
      value: `(${
        value.investmentExecutionCountCurrentYear
      })\n${value.investmentExecutionAmountCurrentYear}`,
      textStyle: amountTextStyle,
    },
  ];
};

// 国からの出資累計額
const getAccumulatedInvestmentsFromTheGovernment = ({
  data,
  isDebugMode,
}: {
  data: z.infer<typeof ReportFundInvestmentModel>;
  isDebugMode: boolean;
}) => {
  if (
    data.accumulatedInvestmentsFromTheGovernmentAccountingTypeNames.length === 0
  ) {
    return formatAmountForPdf(data.accumulatedInvestmentsFromTheGovernment, {
      isDebugMode,
      negativeCount: data.negativeAccumulatedInvestmentsFromTheGovernmentCount,
      rawValue: data.rawAccumulatedInvestmentsFromTheGovernment,
    });
  }
  return `${formatAmountForPdf(data.accumulatedInvestmentsFromTheGovernment, {
    isDebugMode,
    negativeCount: data.negativeAccumulatedInvestmentsFromTheGovernmentCount,
    rawValue: data.rawAccumulatedInvestmentsFromTheGovernment,
  })}\n(${data.accumulatedInvestmentsFromTheGovernmentAccountingTypeNames.join(
    '、',
  )})`;
};

const getInvestmentFundReportPdfTableRow = ({
  data,
  projectDetailUrlType,
  isDebugMode,
}: {
  data: z.infer<typeof ReportFundInvestmentModel>;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
  isDebugMode: boolean;
}): ReportTableCell[] => {
  return [
    {
      type: 'text',
      value: `${data.fundSheetNumber.toString()}${
        data.fundSheetBranchNumber ? `-${data.fundSheetBranchNumber}` : ''
      }`,
      wrapperStyle: rowBaseStyle,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: data.ministryName,
      wrapperStyle: rowBaseStyle,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: data.fundOwner,
      wrapperStyle: rowBaseStyle,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'link',
      href: getProjectDetailPageUrl({
        isPS: projectDetailUrlType === 'public',
        projectId: data.id,
      }),
      value: data.projectName,
      wrapperStyle: rowBaseStyle,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: formatProjectStartYearForPdf(data.startYear),
      wrapperStyle: rowBaseStyle,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'text',
      value: formatProjectEndYearForPdf(data.endYear),
      wrapperStyle: rowBaseStyle,
      textStyle: rowTextBaseStyle,
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        cellRatios: [
          AMOUNT_CELL_RATIOS,
          '71px',
          AMOUNT_CELL_RATIOS,
          AMOUNT_CELL_RATIOS,
        ],
        flexCells: [
          {
            type: 'text',
            value: getAccumulatedInvestmentsFromTheGovernment({
              data,
              isDebugMode,
            }),
            wrapperStyle: { ...rowBaseStyle, display: 'flex' },
            textWrapperStyle: { width: '100%' },
            textStyle: amountTextStyle,
          },
          {
            type: 'text',
            value: formatAmountForPdf(data.investmentAmount2YearsBefore, {
              rawValue: data.rawInvestmentAmount2YearsBefore,
            }),
            wrapperStyle: { ...rowBaseStyle, display: 'flex' },
            textWrapperStyle: { width: '100%' },
            textStyle: amountTextStyle,
          },
          {
            type: 'text',
            value: formatAmountForPdf(data.investmentAmount1YearBefore, {
              rawValue: data.rawInvestmentAmount1YearBefore,
            }),
            wrapperStyle: { ...rowBaseStyle, display: 'flex' },
            textWrapperStyle: { width: '100%' },
            textStyle: amountTextStyle,
          },
          {
            type: 'text',
            value: formatAmountForPdf(data.investmentAmountCurrentYear, {
              rawValue: data.rawInvestmentAmountCurrentYear,
            }),
            wrapperStyle: { ...rowBaseStyle, display: 'flex' },
            textWrapperStyle: { width: '100%' },
            textStyle: amountTextStyle,
          },
        ],
      },
    },
    {
      type: 'text',
      value: formatAmountForPdf(data.accumulatedInvestmentFromPrivateSectors, {
        rawValue: data.rawAccumulatedInvestmentFromPrivateSectors,
      }),
      wrapperStyle: rowBaseStyle,
      textStyle: amountTextStyle,
    },
    {
      type: 'multiple',
      cell: {
        type: 'flex',
        vertical: true,
        flexCells:
          data.fundBusinesses.length > 0
            ? data.fundBusinesses.map((business) => ({
                type: 'multiple',
                cell: {
                  type: 'flex',
                  cellRatios: fundBusinessColumnsCellRatios,
                  flexCells: getFundBusinessColumns(business, isDebugMode),
                },
              }))
            : [
                {
                  type: 'multiple',
                  cell: {
                    type: 'flex',
                    cellRatios: fundBusinessColumnsCellRatios,
                    flexCells: getFundBusinessColumns(undefined, isDebugMode),
                  },
                },
              ],
      },
    },
    {
      type: 'text',
      value: data.overview,
      wrapperStyle: rowBaseStyle,
      textStyle: { ...rowTextBaseStyle, textAlign: 'left' },
      textWrapperStyle: { alignItems: 'flex-start' },
    },
  ];
};

export const getInvestmentFundPdfRowData = ({
  data,
  isDebugMode,
  projectDetailUrlType,
}: {
  data: Array<z.infer<typeof ReportFundInvestmentModel>>;
  isDebugMode: boolean;
  projectDetailUrlType: z.infer<typeof projectDetailUrlTypeEnum>;
}): ReportTableProps['data'] => {
  return data.map((d) => ({
    row: getInvestmentFundReportPdfTableRow({
      data: d,
      isDebugMode,
      projectDetailUrlType,
    }),
    rowStyle: {
      breakInside: 'avoid',
    },
  }));
};
